// src/components/deals/PriceLadder.js
// Prix dégressif par quantité — Maxula
// AbodyStudio Limited
//
// Les prix affichés viennent du SERVEUR (`deal.priceLadder`), jamais d'un calcul
// local. Recalculer ici ferait courir le risque d'annoncer un prix que la
// commande ne facturera pas — un écart de quelques centimes suffit à faire
// perdre confiance au client au moment de payer.

import React from 'react';
import { FiCheck } from 'react-icons/fi';

const fmt = (n) =>
  (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * @param {Array}  ladder    deal.priceLadder — [{ minQty, discountPercent, unitPrice }]
 * @param {number} quantity  quantité choisie
 * @param {Function} onSelect  reçoit la quantité du palier cliqué
 * @param {string} currency
 */
const PriceLadder = ({ ladder, quantity = 1, onSelect, currency = 'MAD' }) => {
  // Un seul niveau = pas de dégressivité : afficher une liste d'une ligne
  // n'apporte rien et encombre la fiche.
  if (!Array.isArray(ladder) || ladder.length < 2) return null;

  // Le palier ACTIF est celui de plus grande minQty atteinte — la même règle
  // que le serveur. Toute autre règle ferait diverger l'affichage du prix payé.
  const activeMinQty = ladder.reduce(
    (acc, t) => (quantity >= t.minQty && t.minQty >= acc ? t.minQty : acc),
    1
  );

  return (
    <div className="mt-4 space-y-2">
      {ladder.map((t, i) => {
        const active = t.minQty === activeMinQty;
        const label =
          t.minQty === 1
            ? 'Découverte x1'
            : i === ladder.length - 1
              ? `${t.minQty}+ achetés`
              : `${t.minQty} achetés`;

        return (
          <button
            key={t.minQty}
            type="button"
            onClick={() => onSelect && onSelect(t.minQty)}
            className={`relative w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border text-left transition ${
              active
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {t.discountPercent > 0 && (
              <span className="absolute -top-2 right-3 px-2 py-0.5 rounded bg-green-600 text-white text-[11px] font-semibold">
                {fmt(t.discountPercent)}% de réduction
              </span>
            )}

            <span className="flex items-center gap-2 text-sm text-gray-800">
              <span
                className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                  active ? 'border-green-600 bg-green-600' : 'border-gray-300'
                }`}
              >
                {active && <FiCheck className="text-white" size={11} />}
              </span>
              {label}
            </span>

            <span className="text-sm whitespace-nowrap">
              {t.discountPercent > 0 && (
                <span className="text-gray-400 line-through mr-2">
                  {fmt(ladder[0].unitPrice)} {currency}
                </span>
              )}
              <span className={active ? 'font-bold text-green-700' : 'font-semibold text-gray-900'}>
                {fmt(t.unitPrice)} {currency}
              </span>
            </span>
          </button>
        );
      })}

      <p className="text-xs text-gray-500">
        Prix unitaire dégressif. La remise s'applique automatiquement dès que la
        quantité atteint le palier.
      </p>
    </div>
  );
};

export default PriceLadder;
